import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
} from '@angular/core';
import { ChatService } from '@features/chat/services/chat.service';
import { User } from '@core/interfaces/user.interface';
import { SvgIconComponent } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '@features/modals/services/modal.service';
import { FormFieldComponent } from '@core/components/form-field/form-field.component';

@Component({
  selector: 'app-admin-user-mute-modal',
  templateUrl: './admin-user-mute-modal.component.html',
  styleUrls: ['./admin-user-mute-modal.component.scss'],
  standalone: true,
  imports: [SvgIconComponent, ReactiveFormsModule, FormFieldComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class AdminUserMuteModalComponent {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  time = new FormControl<number | null>(null, [Validators.required]);
  reason = new FormControl<string>('', [Validators.required]);

  muteForm = new FormGroup({
    time: this.time,
    reason: this.reason,
  });

  user = input<null | User>();

  constructor(
    private chatService: ChatService,
    private toastrService: ToastrService,
    private modalService: ModalService
  ) {
    effect(() => {
      if (!this.user()) {
        this.toastrService.error(
          'User not found, try close popup and open again'
        );
      }
    });
  }

  async onSubmit() {
    const user = this.user();
    this.muteForm.markAllAsTouched();
    this.changeDetectorRef.markForCheck();

    if (!user) {
      this.toastrService.error(
        'User not found, try close popup and open again'
      );
      return;
    }

    if (!this.muteForm.valid) {
      this.toastrService.error('Form is not valid. Please check the inputs.');
      return;
    }

    const time = this.muteForm.get('time')?.value;
    const reason = this.muteForm.get('reason')?.value;

    if (!time || !reason) {
      this.toastrService.error('Time and reason are required');
      return;
    }

    try {
      await this.chatService.muteUser(user!, time!, reason!);
      this.toastrService.success('User has been muted successfully');
      this.modalService.close();
    } catch (error) {
      this.toastrService.error('Failed to mute user. Please try again.');
    }
  }
}
